import React, { useState, useRef } from 'react';
import {
  Container, Typography, Box, Button, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Paper, Snackbar, Alert
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { saveAs } from 'file-saver';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import JSZip from 'jszip';
import { Helmet } from 'react-helmet'

function FileConverter() {
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState('pdf');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const fileInputRef = useRef(null);

  const supportedFormats = [
    { label: 'PDF', value: 'pdf' },
    { label: 'Word (DOCX)', value: 'docx' },
    { label: 'Excel (XLSX)', value: 'xlsx' },
    { label: 'JPG', value: 'jpg' },
    { label: 'PNG', value: 'png' },
    { label: 'Text', value: 'txt' },
  ];

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Conversion functions
  const convertToPDF = async (file, inputExt) => {
    try {
      if (inputExt === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        return htmlToPdf(result.value);
      }
      else if (inputExt === 'xlsx') {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const html = XLSX.utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[0]]);
        return htmlToPdf(html);
      }
      else if (['jpg', 'jpeg', 'png', 'gif'].includes(inputExt)) {
        return imageToPdf(file);
      }
      else if (inputExt === 'pdf') {
        return file;
      }
      else {
        throw new Error(`Unsupported conversion from ${inputExt} to PDF`);
      }
    } catch (error) {
      console.error('PDF conversion error:', error);
      throw error;
    }
  };

  const htmlToPdf = async (html) => {
    const doc = new jsPDF();
    await doc.html(html, {
      async callback(doc) {
        return doc;
      },
      margin: 10,
      width: 180,
      windowWidth: 800
    });
    return doc.output('blob');
  };

  const imageToPdf = async (imageFile) => {
    const imageData = await readFileAsDataURL(imageFile);
    const doc = new jsPDF();
    const imgProps = doc.getImageProperties(imageData);
    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    doc.addImage(imageData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    return doc.output('blob');
  };


  const convertToXlsx = async (file, inputExt) => {
    if (inputExt === 'csv') {
      const text = await file.text();
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(text.split('\n').map(row => row.split(',')));
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const xlsxData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      return new Blob([xlsxData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    }
    throw new Error(`Cannot convert ${inputExt} to XLSX in browser`);
  };


  const convertToImage = async (file, inputExt, outputExt) => {
    if (!['jpg', 'jpeg', 'png'].includes(inputExt)) {
      throw new Error(`Cannot convert ${inputExt} to image`);
    }

    const imageData = await readFileAsDataURL(file);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          resolve(blob);
        }, `image/${outputExt}`, 0.9);
      };
      img.src = imageData;
    });
  };


  const convertToText = async (file, inputExt) => {
    if (inputExt === 'pdf') {
      GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js`;

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument(arrayBuffer).promise;
      let text = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += content.items.map(item => item.str).join(' ') + '\n';
      }

      return new Blob([text], { type: 'text/plain' });
    }
    else if (inputExt === 'docx') {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return new Blob([result.value], { type: 'text/plain' });
    }
    else if (inputExt === 'txt') {
      return file;
    }
    throw new Error(`Cannot convert ${inputExt} to text`);
  };


  const convertToDocx = async (file, inputExt) => {
    try {
      if (inputExt === 'txt') {
        const text = await file.text();
        return createSimpleDocx(text);
      }
      else if (inputExt === 'pdf') {
        // Fallback to text extraction for PDF
        const text = await extractTextFromPdf(file);
        return createSimpleDocx(text);
      }
      else {
        throw new Error(`Unsupported conversion from ${inputExt} to DOCX`);
      }
    } catch (error) {
      console.error('DOCX conversion error:', error);
      throw error;
    }
  };

  const extractTextFromPdf = async (pdfFile) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await getDocument(arrayBuffer).promise;
      let text = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += content.items.map(item => item.str).join(' ') + '\n';
      }

      return text;
    } catch (error) {
      console.error('PDF text extraction error:', error);
      throw new Error('Failed to extract text from PDF');
    }
  };

  const createSimpleDocx = async (text) => {
    const zip = new JSZip();
    zip.file("word/document.xml", `
      <?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
        <w:body>
          <w:p>
            <w:r>
              <w:t>${text}</w:t>
            </w:r>
          </w:p>
        </w:body>
      </w:document>
    `);

    zip.file("[Content_Types].xml", `
      <?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
        <Default Extension="xml" ContentType="application/xml"/>
        <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
        <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
      </Types>
    `);

    const content = await zip.generateAsync({ type: 'blob' });
    return new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
  };

  // Other conversion functions remain the same as previous example...

  const handleConvert = async () => {
    if (!file) {
      showSnackbar('Please select a file first', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const inputExt = file.name.split('.').pop().toLowerCase();
      const outputExt = targetFormat.toLowerCase();
      const fileName = file.name.split('.').slice(0, -1).join('.');

      if (inputExt === outputExt) {
        showSnackbar('Source and target formats are the same', 'warning');
        return;
      }

      let result;

      if (outputExt === 'pdf') {
        result = await convertToPDF(file, inputExt);
      }
      else if (outputExt === 'docx') {
        result = await convertToDocx(file, inputExt);
      }
      else if (outputExt === 'xlsx') {
        result = await convertToXlsx(file, inputExt);
      }
      else if (['jpg', 'png'].includes(outputExt)) {
        result = await convertToImage(file, inputExt, outputExt);
      }
      else if (outputExt === 'txt') {
        result = await convertToText(file, inputExt);
      } else {
        throw new Error('Unsupported conversion');
      }

      saveAs(result, `${fileName}.${outputExt}`);
      showSnackbar('File converted successfully!', 'success');
    } catch (error) {
      console.error('Conversion error:', error);
      showSnackbar(`Conversion failed: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Helmet>
  <html lang="en" />
  <title>File Converter</title>
  <meta
    name="description"
    content="Convert between PDF, Word, Excel, JPG, PNG and other formats quickly and securely with Tooliso’s free file converter."
  />
  <link rel="canonical" href="https://tooliso.com/FileConverter" />

  <meta property="og:title" content="Tooliso File Converter – Free & Secure" />
  <meta
    property="og:description"
    content="Convert documents and images instantly online. Tooliso supports PDF, Word, Excel, JPG, PNG, and more. No software needed."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://tooliso.com/FileConverter" />

  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Free File Converter – Convert Files Online | Tooliso" />
  <meta
    name="twitter:description"
    content="Convert PDF, Word, Excel, images, and more in seconds using Tooliso’s secure online file converter."
  />
</Helmet>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          File Format Converter
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" sx={{ mb: 4 }}>
          Convert between formats directly in your browser
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              fullWidth
              sx={{ py: 2 }}
            >
              {file ? file.name : 'Select File'}
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                ref={fileInputRef}
              />
            </Button>
          </Box>

          <FormControl fullWidth>
            <InputLabel id="target-format-label">Convert To</InputLabel>
            <Select
              labelId="target-format-label"
              value={targetFormat}
              label="Convert To"
              onChange={(e) => setTargetFormat(e.target.value)}
            >
              {supportedFormats.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleConvert}
            disabled={isLoading || !file}
            sx={{ py: 2 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                Converting...
              </>
            ) : (
              'Convert File'
            )}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default FileConverter;